import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Share } from 'lucide-react';
import {
  isInstallAvailable,
  isStandalone,
  isIos,
  triggerInstall,
  onAvailabilityChange,
  isInstallBannerDismissed,
  dismissInstallBanner,
} from '../lib/installPrompt';

/**
 * Shows an install-to-home-screen banner.
 * - Android/Chrome/Edge: native prompt via beforeinstallprompt
 * - iOS Safari: instructions for "Share → Add to Home Screen"
 * - Already installed (standalone) or dismissed: hidden
 */
export default function InstallBanner({ visitCount = 1, minVisits = 2, brandName = 'Memouraa', accentColor = '#D4AF37' }) {
  const [available, setAvailable] = useState(isInstallAvailable());
  const [dismissed, setDismissed] = useState(isInstallBannerDismissed());

  useEffect(() => {
    return onAvailabilityChange((avail) => setAvailable(avail));
  }, []);

  if (dismissed) return null;
  if (isStandalone()) return null;
  if (visitCount < minVisits) return null;

  const handleInstall = async () => {
    const outcome = await triggerInstall();
    if (outcome === 'accepted' || outcome === 'dismissed') {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    dismissInstallBanner();
    setDismissed(true);
  };

  const ios = isIos();
  if (!available && !ios) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: 'spring', damping: 22 }}
        className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm"
      >
        <div className="bg-midnight-light border rounded-2xl p-4 shadow-2xl flex items-start gap-3"
          style={{ borderColor: `${accentColor}40` }}
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${accentColor}20` }}
          >
            <Download className="w-5 h-5" style={{ color: accentColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-text-light font-medium text-sm">
              Add {brandName} to your home screen
            </p>
            {ios && !available ? (
              <p className="text-text-muted text-xs mt-1 leading-relaxed">
                In Safari, tap <Share className="w-3 h-3 inline align-text-bottom" /> Share, then "Add to Home Screen".
              </p>
            ) : (
              <p className="text-text-muted text-xs mt-1">
                One-tap access during the event.
              </p>
            )}
            {!ios && available && (
              <button
                onClick={handleInstall}
                className="mt-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                style={{ backgroundColor: accentColor, color: '#1A1B2E' }}
              >
                Install
              </button>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="p-2.5 text-text-muted hover:text-text-light transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
